import type { MCPServer } from "@/schemas/agent.schema";
import type { McpConnection } from "@/schemas/mcp-connection.schema";
import type { RequestConfig } from "@/services/api";

import { mcpConnectionsApi } from "@/api/mcp-connections.api";

/** An entry can still carry a `credentials_version` an older server persisted. */
export type ExportedMcpServer = MCPServer & { credentials_version?: number };

interface StoredMcpServer extends ExportedMcpServer {}

export interface ResolvedImportedMcpServers {
  servers: MCPServer[];
  unresolvedCount: number;
}

/** Mirrors `STRICT_SECRET_PLACEHOLDER_REGEX` in the server's secrets resolver. */
const SECRET_PLACEHOLDER = /^\{\{\$[A-Z_][A-Z0-9_]*\}\}$/;

/**
 * Export sanitizer. The URL is the portable identity of an MCP server; a
 * `connection_id` is only valid inside the source project, and credentials
 * never travel in the JSON. Connection references are dropped and every header
 * value is blanked unless it is a whole `{{$SECRET}}` placeholder, which the
 * destination project resolves against its own secrets.
 */
export function sanitizeMcpServersForExport(servers: ExportedMcpServer[]): MCPServer[] {
  return servers.map((server) => {
    const {
      connection_id: _connectionId,
      credentials_version: _credentialsVersion,
      headers,
      ...rest
    } = server;
    return { ...rest, headers: sanitizeHeaders(headers) };
  });
}

function sanitizeHeaders(headers: Record<string, string> | undefined): Record<string, string> {
  if (!headers)
    return {};
  return Object.fromEntries(
    Object.entries(headers).map(([name, value]) => [
      name,
      SECRET_PLACEHOLDER.test(value) ? value : "",
    ]),
  );
}

/**
 * Import resolver, pure so it is testable without any request. For each server:
 * keep a `connection_id` already visible in this project, re-link by URL
 * otherwise, or leave the entry disconnected (`disabled`) so the operator can
 * click Conectar. The imported tool allowlist and disabled flag survive a
 * re-link; only the unresolved branch overrides `disabled`.
 */
export function resolveImportedMcpServers(
  servers: MCPServer[],
  connections: McpConnection[],
): ResolvedImportedMcpServers {
  let unresolvedCount = 0;

  const resolved = servers.map((server) => {
    const byId = server.connection_id
      ? connections.find(connection => connection.id === server.connection_id)
      : undefined;
    if (byId)
      return withConnection(dropConnectionFields(server), byId.id);

    const byUrl = server.url
      ? connections.find(connection => connection.url === server.url)
      : undefined;
    if (byUrl)
      return withConnection(dropConnectionFields(server), byUrl.id);

    unresolvedCount++;
    return { ...dropConnectionFields(server), disabled: true };
  });

  return { servers: resolved, unresolvedCount };
}

/**
 * Fetch the project's connections once and resolve every server against them.
 * The caller passes the active-project `RequestConfig` (`X-Project-ID`), so an
 * org-scope operator does not see another project's connections. A failed list
 * request never blocks an import: every server falls into the "needs
 * connecting" branch instead.
 */
export async function resolveImportedMcpServersFromApi(
  servers: MCPServer[],
  config?: RequestConfig,
): Promise<ResolvedImportedMcpServers> {
  try {
    return resolveImportedMcpServers(servers, await mcpConnectionsApi.list(config));
  }
  catch {
    return resolveImportedMcpServers(servers, []);
  }
}

function dropConnectionFields(server: MCPServer): MCPServer {
  const copy: StoredMcpServer = { ...server };
  delete copy.connection_id;
  delete copy.credentials_version;
  return copy;
}

function withConnection(server: MCPServer, connectionId: string): MCPServer {
  return { ...server, connection_id: connectionId };
}
